<template>
	<div class="action-flow-param" :class="paramClass" @click="toggleHint" ref="paramElement">
		<div class="param-content">
			<span class="param-label">{{ label }}</span>
			<span class="param-preview" v-if="!isExpanded && inputValue">{{ inputValue }}</span>
		</div>
		<div class="param-hint-toggle" :class="{ 'expanded': isExpanded }" v-if="hint">
			<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
				<path d="M6 9l6 6 6-6"/>
			</svg>
		</div>
		<div class="param-hint-tooltip" :class="{ 'visible': isExpanded, 'expand-width': paramInputType === 'icon' }" v-if="hint" @click.stop>
			{{ hint }}
			<!-- Input field inside the hint -->
			<div class="param-input-container" v-if="paramInputType">
				<label class="param-input-label" v-if="paramInputLabel">{{ paramInputLabel }}</label>
				<input 
					v-if="paramInputType === 'text'"
					type="text" 
					class="param-input-field"
					:placeholder="paramInputLabel"
					v-model="inputValue"
					@change="handleInputChange"
				/>
				<textarea 
					v-else-if="paramInputType === 'textarea'"
					class="param-input-field param-textarea"
					:placeholder="paramInputLabel"
					v-model="inputValue"
					@change="handleInputChange"
					rows="3"
				></textarea>
				<select 
					v-else-if="paramInputType === 'select'"
					class="param-input-field"
					v-model="inputValue"
					@change="handleInputChange"
				>
					<option value="" disabled>{{ paramInputLabel || 'Select an option' }}</option>
					<option 
						v-for="option in paramInputOptions" 
						:key="option.value" 
						:value="option.value"
					>
						{{ option.label }}
					</option>
				</select>
				<AdvTextbox
					v-else-if="paramInputType === 'adv-text'"
					:label="paramInputLabel"
					v-model="inputValue"
					:custom-tokens="advTextCustomTokens"
					@update:modelValue="handleInputChange"
				/>
				<DxLookup
					v-else-if="paramInputType === 'icon'"
					:data-source="icons" item-template="iconItem" field-template="iconField"
					:search-enabled="true" :show-clear-button="true" :use-popover="true"
					:drop-down-options="{ wrapperAttr: { class: 'popup-iconselect' }, deferRendering: false }"
					v-model="inputValue"
					@value-changed="handleInputChange"
				>
					<template #iconItem="{ data }">
						<i v-if="data" :class="'dx-icon-' + data"></i>
						<span v-if="data">{{ data }}</span>
					</template>
					<template #iconField="{ data }">
						<i v-if="data" :class="'dx-icon-' + data" style="font-size: 18px;"></i>
						<span v-if="data" style="margin-left: 10px;">{{ data }}</span>
					</template>
				</DxLookup>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
import AdvTextbox from '../adv-textbox.vue'
import icons from "../../data/icons";
import { DxLookup } from 'devextreme-vue/lookup';

const props = defineProps({
	label: {
		type: String,
		required: true
	},
	hint: {
		type: String,
		default: ''
	},
	paramClass: {
		type: String,
		default: ''
	},
	paramInputType: {
		type: String,
		default: '',
		validator: (value) => ['', 'text', 'select', 'textarea', 'adv-text', 'icon'].includes(value)
	},
	paramInputLabel: {
		type: String,
		default: ''
	},
	paramInputOptions: {
		type: Array,
		default: () => []
	},
	paramInputValue: {
		type: [String, Number, Boolean, Object, Array],
		default: null
	}
})

const emit = defineEmits(['paramChange'])

const isExpanded = ref(false);
const paramElement = ref(null);
const inputValue = ref('');

// Watch for changes to paramInputValue and update inputValue accordingly
watch(() => props.paramInputValue, (newValue) => {
	inputValue.value = newValue === null ? '' : newValue;
}, { immediate: true });

// Extract custom tokens for adv-textbox from paramInputOptions
const advTextCustomTokens = computed(() => {
	if (props.paramInputType !== 'adv-text' || !props.paramInputOptions) {
		return {}
	}
	
	// Look for a customTokens property in the options
	const customTokensOption = props.paramInputOptions.find(option => option.customTokens)
	return customTokensOption?.customTokens || {}
});

const toggleHint = () => {
	if (props.hint) {
		isExpanded.value = !isExpanded.value
	}
}

const handleInputChange = () => {
	// Emit the parameter change with structured data
	emit('paramChange', {
		label: props.label,
		paramClass: props.paramClass,
		value: inputValue.value,
		inputType: props.paramInputType,
		inputLabel: props.paramInputLabel
	})
}

// Close hint when clicking outside
const handleClickOutside = (event) => {
	// Use nextTick to ensure the toggle has been processed first
	nextTick(() => {
		if (isExpanded.value && paramElement.value && !paramElement.value.contains(event.target)) {
			isExpanded.value = false
		}
	})
}

onMounted(() => {
	document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
	document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.action-flow-param {
	cursor: pointer;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 36px;
	line-height: 36px;
}

.param-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex: 1;
	min-width: 0; /* Allow text truncation */
}

.param-label {
	font-weight: 500;
    white-space: nowrap;
}

.param-preview {
	font-size: 11px;
	color: #999;
	font-style: italic;
	margin-left: 8px;
	opacity: 0.95;
	max-width: 120px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	line-height: 36px;
	vertical-align: middle;
}

.param-hint-toggle {
	opacity: 0.7;
	transition: all 0.2s ease;
	margin-left: 8px;
}

.param-hint-toggle.expanded {
	opacity: 1;
	transform: rotate(180deg);
}

.action-flow-param:hover .param-hint-toggle {
	opacity: 1;
}

.param-hint-tooltip {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: rgba(255, 255, 255, 0.98);
	border: 1px solid #e1e5e9;
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	padding: 8px 12px;
	font-size: 11px;
	color: #666;
	line-height: 1.4;
	max-width: 250px;
	word-wrap: break-word;
	z-index: 3000;
	opacity: 0;
	visibility: hidden;
	transform: translateY(-10px);
	transition: all 0.2s ease;
}

.param-hint-tooltip.visible {
	opacity: 1;
	visibility: visible;
	transform: translateY(0);
}

.param-hint-tooltip.expand-width {
	width: 400px;
}

/* Arrow pointing up */
.param-hint-tooltip::before {
	content: '';
	position: absolute;
	top: -4px;
	left: 12px;
	width: 0;
	height: 0;
	border-left: 4px solid transparent;
	border-right: 4px solid transparent;
	border-bottom: 4px solid #e1e5e9;
}

.param-hint-tooltip::after {
	content: '';
	position: absolute;
	top: -3px;
	left: 12px;
	width: 0;
	height: 0;
	border-left: 4px solid transparent;
	border-right: 4px solid transparent;
	border-bottom: 4px solid rgba(255, 255, 255, 0.98);
}

/* Input field styles */
.param-input-container {
	margin-top: 8px;
	padding: 0 0px;
}

.param-input-label {
	border-top: 1px solid #e1e5e9;
    padding-top: 4px;
    display: block;
	font-size: 11px;
	color: #666;
	margin-bottom: 4px;
	font-weight: 600;
    text-align: center;
}

.param-input-field {
	width: 100%;
	padding: 6px 8px;
	border: 1px solid #e1e5e9;
	border-radius: 4px;
	font-size: 12px;
	background: #fff;
	transition: border-color 0.2s ease;
}

.param-input-field:focus {
	outline: none;
	border-color: #007acc;
	box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.1);
}

.param-input-field::placeholder {
	color: #999;
}

.param-textarea {
	resize: vertical;
	min-height: 60px;
	font-family: inherit;
	line-height: 1.4;
}
</style> 